/**
 * Resumo da personalização da IA em Configurações. Não edita — conta quantas
 * preferências e prompts existem e leva à tela dedicada (mesmo papel do
 * TimbradoSummaryCard).
 *
 * Some para quem não tem a feature `peticao_ia`: sem chat, não há o que
 * personalizar — e as rotas respondem 403 de qualquer forma.
 */
import { ChevronRight, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { usePreferenciasIa, usePromptsIa } from '@/hooks/useIaPersonalizacao';
import { useFeatureLiberada } from '@/hooks/useSubscription';
import { classifyError } from '@/lib/errors';

const PERSONALIZACAO_PAGE = '/app/configuracoes/ia';

function plural(n: number, singular: string, pluralForma: string): string {
  return `${n} ${n === 1 ? singular : pluralForma}`;
}

export function PersonalizacaoIaSummaryCard() {
  const navigate = useNavigate();
  const iaLiberada = useFeatureLiberada('peticao_ia');
  const preferencias = usePreferenciasIa(iaLiberada);
  const prompts = usePromptsIa(iaLiberada);

  if (!iaLiberada) return null;

  const carregando = preferencias.isLoading || prompts.isLoading;
  const erro = preferencias.error ?? prompts.error;
  const ativas = (preferencias.data?.preferencias ?? []).filter((p) => p.ativo).length;
  const totalPrompts = prompts.data?.prompts.length ?? 0;
  const vazio = ativas === 0 && totalPrompts === 0;

  return (
    <Card className="border bg-card">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <CardTitle className="text-base">Personalização da IA</CardTitle>
        </div>
        <CardDescription>
          Instruções que a IA sempre segue e pedidos que você reaproveita no chat
        </CardDescription>
      </CardHeader>
      <CardContent>
        {carregando ? (
          <div className="flex justify-center py-4">
            <Spinner size={32} label="Carregando personalização" />
          </div>
        ) : erro ? (
          <Alert variant="destructive">
            <AlertDescription>{classifyError(erro).message}</AlertDescription>
          </Alert>
        ) : (
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              {vazio
                ? 'Você ainda não personalizou a IA.'
                : `${plural(ativas, 'preferência ativa', 'preferências ativas')} · ${plural(
                    totalPrompts,
                    'prompt salvo',
                    'prompts salvos',
                  )}`}
            </p>
            <Button
              variant="outline"
              className="w-full sm:w-auto"
              onClick={() => navigate(PERSONALIZACAO_PAGE)}
            >
              {vazio ? 'Configurar' : 'Gerenciar'}
              <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
